import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";

/** Each animation frame reveals this fraction of the unrevealed backlog (at least one character). */
const BACKLOG_FRACTION_PER_FRAME = 1 / 8;
/** Cap React re-renders while catching up (e.g. markdown parse during streaming). */
const MIN_LENGTH_UPDATE_INTERVAL_MS = 50;

/**
 * Progressively reveal streamed text so bursty network chunks read as a steady stream.
 *
 * The reveal rate scales with the backlog, so the text trails the stream by a fraction of a
 * second at most. Shows the full text immediately when reduced motion is preferred.
 */
export function useSmoothText(target: string): string {
  const reduceMotion = Boolean(useReducedMotion());
  const [length, setLength] = useState(reduceMotion ? target.length : 0);
  const lengthRef = useRef(length);
  const targetLengthRef = useRef(target.length);
  const lastLengthUpdateAtRef = useRef(-MIN_LENGTH_UPDATE_INTERVAL_MS);
  targetLengthRef.current = target.length;

  useEffect(() => {
    if (reduceMotion) {
      lengthRef.current = target.length;
      setLength(target.length);
      return;
    }
    lengthRef.current = Math.min(lengthRef.current, target.length);
    lastLengthUpdateAtRef.current = -MIN_LENGTH_UPDATE_INTERVAL_MS;
    let frame = 0;
    const tick = () => {
      const backlog = targetLengthRef.current - lengthRef.current;
      if (backlog <= 0) {
        return;
      }
      lengthRef.current += Math.max(1, Math.ceil(backlog * BACKLOG_FRACTION_PER_FRAME));
      const remaining = targetLengthRef.current - lengthRef.current;
      const now = performance.now();
      if (remaining <= 0 || now - lastLengthUpdateAtRef.current >= MIN_LENGTH_UPDATE_INTERVAL_MS) {
        lastLengthUpdateAtRef.current = now;
        setLength(lengthRef.current);
      }
      if (remaining > 0) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, reduceMotion]);

  return target.slice(0, Math.min(length, target.length));
}
